import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import { programKeys } from './usePrograms';
import type { ProgramStage } from '@lms/shared/types';

export const stageKeys = {
  all: ['program-stages'] as const,
  lists: () => [...stageKeys.all, 'list'] as const,
  list: (levelId: string) => [...stageKeys.all, 'list', levelId] as const,
  details: () => [...stageKeys.all, 'detail'] as const,
  detail: (id: string) => [...stageKeys.details(), id] as const,
};

export function useStages(levelId: string) {
  return useQuery({
    queryKey: stageKeys.list(levelId),
    queryFn: () => apiClient.programStages.list(levelId),
    enabled: !!levelId,
  });
}

export function useCreateStage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      programId: string;
      data: Partial<ProgramStage> & { levelId: string; name: string };
    }) => apiClient.programStages.create(variables.data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: programKeys.detail(variables.programId),
      });
      void queryClient.invalidateQueries({ queryKey: stageKeys.lists() });
    },
  });
}

export function useUpdateStage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: { id: string; programId: string; data: Partial<ProgramStage> }) =>
      apiClient.programStages.update(variables.id, variables.data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: programKeys.detail(variables.programId),
      });
      void queryClient.invalidateQueries({ queryKey: stageKeys.lists() });
    },
  });
}

export function useDeleteStage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: { id: string; programId: string }) =>
      apiClient.programStages.delete(variables.id),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: programKeys.detail(variables.programId),
      });
      void queryClient.invalidateQueries({ queryKey: stageKeys.lists() });
    },
  });
}
